'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LandingPageHeading } from '../shared/landing-page-heading';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { Button } from '../ui/button';
import { GoArrowUpRight } from 'react-icons/go';
import { LandingPageContactForm } from './landing-page-contact-form';
import { useCurrentStore } from '../../utils/use-store-utils';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface UniversalContactProps {
  showBilling?: boolean;
  showButton?: boolean;
  generalLayout: GeneralLayout;
  badge: string;
  title: string;
  headingAs?: 'h1' | 'h2' | 'h3';
  description: string;
  clasName?: {
    section?: string;
    container?: string;
  };
  breadcrumbs?: {
    name: string;
  };
}

export function UniversalContact({
  showBilling = false,
  showButton = true,
  generalLayout,
  badge,
  title,
  headingAs,
  description,
  clasName,
  breadcrumbs,
}: UniversalContactProps) {
  const { t } = useTranslations();
  const { isGasGas, isCFMoto } = useCurrentStore();
  const BillingHeading = headingAs === 'h1' ? 'h2' : 'h4';

  const contacts = [
    {
      image: { url: '/images/icons/smartphone.svg' },
      label: 'Zavolajte nám',
      value: generalLayout.data.contact?.phone,
      href: `tel:${generalLayout.data.contact?.phone}`,
    },
    {
      image: { url: '/images/icons/send.svg' },
      label: 'Napíšte mám',
      value: generalLayout.data.contact?.email,
      href: `mailto:${generalLayout.data.contact?.email}`,
    },
  ];

  return (
    <section className={cn('custom-section', 'gap-8 sm:gap-10 lg:gap-12', clasName?.section)}>
      {breadcrumbs && (
        <div className={cn('custom-container', 'flex items-center justify-start gap-2.5')}>
          <Link
            prefetch={false}
            href={ROUTES.HOME()}
            className="text-sm font-normal text-primary sm:text-base"
          >
            Domov
          </Link>
          <span className="text-sm text-black/30 sm:text-base">/</span>
          <p className="text-sm text-black sm:text-base">{breadcrumbs?.name}</p>
        </div>
      )}

      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-16 md:flex-row md:items-center md:justify-between md:gap-8 lg:gap-16',
          clasName?.container,
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-8 md:max-w-[540px]">
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <LandingPageHeading as={headingAs} badge={badge} title={title} />
            <HtmlRenderer
              html={description}
              className={cn('w-full', 'ea-about-section__content')}
            />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4 lg:gap-5">
            {contacts.map(contact => (
              <Link
                prefetch={false}
                key={contact.label}
                href={contact.href}
                target="_blank"
                className="flex w-full items-start justify-start gap-4"
              >
                <div className="flex min-h-12 min-w-12 items-center justify-center border border-black/10 lg:min-h-[52px] lg:min-w-[52px]">
                  <Image
                    src={contact.image.url}
                    alt={resolveImageAlt(contact?.image, contact?.label)}
                    width={24}
                    height={24}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-normal text-black/70 sm:text-sm">
                    {contact.label}
                  </span>
                  <strong className="text-base font-semibold sm:text-lg lg:text-xl">
                    {contact.value}
                  </strong>
                </div>
              </Link>
            ))}
          </div>
          {showBilling && (
            <div className="flex flex-col gap-4">
              <BillingHeading className="text-lg font-semibold tracking-tight sm:text-xl lg:text-2xl">
                {t('text679')}
              </BillingHeading>
              <p className="text-sm text-black/70 sm:text-base">
                {generalLayout.data.contact?.company} <br />
                {generalLayout.data.contact?.address} <br />
                {generalLayout.data.contact?.postcode} {generalLayout.data.contact?.city} <br />
                IČO: {generalLayout.data.contact?.ico}
              </p>
            </div>
          )}
          {showButton && (
            <Button
              asChild
              className={cn(
                'w-full sm:w-fit',
                isGasGas && '!text-white',
                isCFMoto && '!text-white',
              )}
            >
              <Link prefetch={false} href={'/kontakt'}>
                Prejsť na kontakty <GoArrowUpRight size={20} />
              </Link>
            </Button>
          )}
        </div>
        <LandingPageContactForm />
      </div>
    </section>
  );
}
